<template>
  <nav class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm fixed z-10" style="padding-top: constant(safe-area-inset-top); padding-top: env(safe-area-inset-top);">
    <div class="w-full">
      <div class="flex justify-between items-center h-16 px-2">
        <!-- Left side -->
        <div class="flex items-center flex-1 min-w-0">
          <div class="px-2 sm:px-4 py-2 min-w-0 flex-1">
            <h1 class="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 truncate">{{ currentMenuTitle }}</h1>
            <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{{ currentMenuSubtitle }}</p>
          </div>
        </div>
        
        <!-- Right side -->
        <div v-if="isAuthenticated" class="flex-shrink-0 mr-4">
          <!-- Mobile : icônes compte + thème + logout -->
          <div v-if="isMobile" class="flex items-center justify-center h-full space-x-2">
            <button @click="openAccount" class="p-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none transition-colors" title="Mon compte">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path fill-rule="evenodd" d="M12 2.25a5.25 5.25 0 100 10.5 5.25 5.25 0 000-10.5zM3.75 20.25a8.25 8.25 0 1116.5 0v.75H3.75v-.75z" clip-rule="evenodd" />
              </svg>
            </button>
            <Tooltip placement="bottom" width="18rem" align="right">
              <button
                @click="cycleTheme"
                :disabled="isChangingTheme"
                class="p-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                :aria-label="themeToggleLabel"
              >
                <span class="w-5 h-5" v-html="sanitizeHtml(currentThemeIcon)"></span>
              </button>
              <template #content>{{ themeToggleLabel }}</template>
            </Tooltip>
            <button @click="handleLogout" class="bg-gray-100 dark:bg-gray-800 text-red-600 dark:text-red-400 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-red-700 dark:hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors duration-200 flex items-center justify-center" title="Déconnexion">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <!-- Porte rectangulaire -->
                <rect x="3" y="3" width="10" height="18" rx="1.5" />
                <!-- Flèche sortante vers la droite -->
                <path d="M15 12h5m0 0l-2.5-2.5M20 12l-2.5 2.5" />
              </svg>
            </button>
          </div>
          <!-- Desktop : bouton Compte + bouton Thème + bouton Déconnexion -->
          <div v-else class="flex items-center justify-center space-x-4 h-full">
            <button
              @click="openAccount"
              class="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              {{ displayName }}
            </button>
            <Tooltip placement="bottom" width="18rem" align="right">
              <button
                @click="cycleTheme"
                :disabled="isChangingTheme"
                class="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                :aria-label="themeToggleLabel"
              >
                <span class="w-5 h-5" v-html="sanitizeHtml(currentThemeIcon)"></span>
              </button>
              <template #content>{{ themeToggleLabel }}</template>
            </Tooltip>
            <button
              @click="handleLogout"
              class="bg-gray-100 dark:bg-gray-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 flex items-center justify-center"
              title="Déconnexion"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <!-- Porte rectangulaire -->
                <rect x="3" y="3" width="10" height="18" rx="1.5" />
                <!-- Flèche sortante vers la droite -->
                <path d="M15 12h5m0 0l-2.5-2.5M20 12l-2.5 2.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { defineComponent, computed } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useThemeStore } from '@/stores/themeStore'
import { storeToRefs } from 'pinia'
import { useRouter, useRoute } from 'vue-router'
import { useLogger } from '@/utils/logger'
import { getActiveMenuItem } from '@/config/menuItems'
import Tooltip from '@/components/common/Tooltip.vue'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

export default defineComponent({
  name: 'Navbar',
  components: {
    Tooltip
  },
  emits: ['toggle-sidebar', 'open-account'],
  props: {
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    const themeStore = useThemeStore()
    const { isAuthenticated, user } = storeToRefs(authStore)
    const { themeMode, isChangingTheme } = storeToRefs(themeStore)
    const logger = useLogger('Navbar')

    const handleLogout = async () => {
      logger.debug('Début du processus de déconnexion')
      logger.debug('État auth avant logout:', {
        isAuthenticated: authStore.isAuthenticated,
        user: authStore.user
      })

      await authStore.logout()

      logger.debug('État auth après logout:', {
        isAuthenticated: authStore.isAuthenticated,
        user: authStore.user
      })

      logger.debug('Redirection vers /login')
      router.replace('/login')
    }

    const openAccount = () => {
      emit('open-account')
    }

    const displayName = computed(() => user.value?.name || user.value?.email || 'Compte')

    const themeToggleLabel = computed(() => {
      if (themeMode.value === 'light') {
        return 'Thème clair actif — cliquer pour passer en mode sombre'
      }
      if (themeMode.value === 'dark') {
        return 'Thème sombre actif — cliquer pour passer en mode système'
      }
      return 'Thème système actif (suit les réglages de l\'appareil) — cliquer pour passer en mode clair'
    })

    const cycleTheme = () => {
      if (isChangingTheme.value) return
      themeStore.cycleThemeMode()
    }

    const sunIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
      <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
    </svg>`

    const moonIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
      <path fill-rule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clip-rule="evenodd" />
    </svg>`

    const systemIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
      <path fill-rule="evenodd" d="M2.25 5.25a3 3 0 013-3h13.5a3 3 0 013 3v8.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V5.25zm4.5 0a.75.75 0 00-.75.75v.75c0 .414.336.75.75.75h10.5a.75.75 0 00.75-.75v-.75a.75.75 0 00-.75-.75H6.75zm-.75 3.75a.75.75 0 01.75-.75h10.5a.75.75 0 01.75.75v6a.75.75 0 01-.75.75H6.75a.75.75 0 01-.75-.75v-6zm9 9.75a.75.75 0 00-.75.75v.75a.75.75 0 00.75.75h1.5a.75.75 0 00.75-.75v-.75a.75.75 0 00-.75-.75h-1.5z" clip-rule="evenodd" />
    </svg>`

    const currentThemeIcon = computed(() => {
      if (themeMode.value === 'light') return sunIcon
      if (themeMode.value === 'dark') return moonIcon
      return systemIcon
    })

    // Menu actif basé sur la route courante
    const activeMenuItem = computed(() => {
      if (!isAuthenticated.value) {
        return null
      }
      return getActiveMenuItem(route.path)
    })

    const currentMenuTitle = computed(() => {
      if (activeMenuItem.value) {
        return activeMenuItem.value.name
      }
      return 'K&C Portfolio Manager'
    })

    const currentMenuSubtitle = computed(() => {
      if (activeMenuItem.value) {
        return activeMenuItem.value.subtitle
      }
      return 'Gérez vos investissements'
    })

    return {
      isAuthenticated,
      user,
      displayName,
      handleLogout,
      openAccount,
      isChangingTheme,
      themeToggleLabel,
      cycleTheme,
      currentThemeIcon,
      sanitizeHtml,
      currentMenuTitle,
      currentMenuSubtitle
    }
  }
})
</script>
